import React, { useContext, useMemo } from 'react';
import {
    IWorkStream, WorkStream, Contributor, CustomField, EntityValidations,
    ISpecificEntityFormProps
} from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from '../cr/CrDropdown';
import { CrUserPicker } from '../cr/CrUserPicker';
import { EntityStatus } from '../../refData/EntityStatus';
import { CrTextField } from '../cr/CrTextField';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { CrComboBox } from '../cr/CrComboBox';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { EntityFormWS } from '../EntityFormWS';
import { WorkStreamTaskManagementOptions } from '../../types/WorkStream';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';

export class WorkStreamValidations extends EntityValidations {
    public ProjectID: string = null;
    public NormalContributors: string = null;
}

export const WorkStreamForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { customFieldDfns, entityStatuses, projects, userProjects, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => projects(), [projects]);
    useMemo(() => userProjects(), [userProjects]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (workStream: IWorkStream): Promise<WorkStreamValidations> => {
        const errors = new WorkStreamValidations();

        if (workStream.Title === null || workStream.Title === '') {
            errors.Title = 'Work stream name is required';
            errors.Valid = false;
        }
        else
            errors.Title = null;

        if (workStream.ProjectID === null) {
            errors.ProjectID = 'Project is required';
            errors.Valid = false;
        }
        else
            errors.ProjectID = null;

        if (workStream.Contributors.some(ct => ct.ContributorUserID == workStream.LeadUserID)) {
            errors.NormalContributors = 'A user cannot be both the lead and a contributor';
            errors.Valid = false;
        }
        else
            errors.NormalContributors = null;

        return Promise.resolve(errors);
    };

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'All Delivery Apps - WorkStreams',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    return (
        <EntityFormWS<IWorkStream, WorkStreamValidations>
            {...props}
            entityName="Work stream"
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: workStream, ValidationErrors: errors } = formState;
                // const mappedUsers = LookupService.projectUsers(lookupData, workStream.ProjectID, workStream);
                // const suggestions = {
                //     initialSuggestionsHeaderText: `Project users`,
                //     initialSuggestions: mappedUsers,
                //     noResultsFoundText: mappedUsers.length === 0 ? `No users are mapped to this work stream's project` : null
                // };
                return (
                    <div>
                        {workStream.ID > 0 && workStream.TaskManagementOptionID === WorkStreamTaskManagementOptions.ExternalTaskManagement && <CrTextField
                            label="Name of external app"
                            className={styles.formField}
                            maxLength={255}
                            value={workStream.ExternalAppName}
                            onChange={(e, v) => changeHandlers.changeTextField(v, 'ExternalAppName')}
                        />}
                        <CrTextField
                            label="Name"
                            required={true}
                            maxLength={255}
                            className={styles.formField}
                            value={workStream.Title}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'Title')}
                            errorMessage={errors.Title}
                        />
                        <CrTextField
                            label="Description"
                            className={styles.formField}
                            value={workStream.Description}
                            maxLength={500}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'Description')}
                            multiline
                        />
                        {/* <CrTextField
                            label="Work stream ID"
                            maxLength={255}
                            className={styles.formField}
                            value={workStream.WorkStreamCode}
                            onChange={(_, v) => changeHandlers.changeTextField(v, 'WorkStreamCode')}
                        /> */}
                        <CrUserPicker
                            label="Lead (L)"
                            className={styles.formField}
                            disabled={!workStream.ProjectID}
                            users={lookupData.Users?.All}
                            selectedUsers={workStream.LeadUserID && [workStream.LeadUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'LeadUserID')}
                        //{...suggestions}
                        />
                        <CrUserPicker
                            label="Contributors (C)"
                            className={styles.formField}
                            disabled={!workStream.ProjectID}
                            users={lookupData.Users?.All}
                            itemLimit={3}
                            selectedUsers={workStream.Contributors && workStream.Contributors.map(c => c.ContributorUserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                            errorMessage={errors.NormalContributors}
                        //{...suggestions}
                        />

                        <CrCustomFields
                            label="Additional Information"
                            className={styles.formField}
                            customFieldDfns={filteredCustomFieldDfns}
                            selectedItems={LookupService.customFieldsToDropdownWithText(workStream.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />

                        {formState.FormDataBeforeChanges.EntityStatusID === EntityStatus.Closed &&
                            <CrDropdown
                                label="Status"
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                selectedKey={workStream.EntityStatusID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'EntityStatusID')}
                            />
                        }
                    </div>
                );
            }}
            loadEntity={id => dataServices.workStreamService.read(id, true, true)}
            loadNewEntity={() => new WorkStream()}
            loadEntityValidations={() => new WorkStreamValidations()}
            onValidateEntity={validateEntity}
            onCreate={w => dataServices.workStreamService.create(w)}
            onUpdate={w => dataServices.workStreamService.update(w.ID, w)}
            parentEntities={dataServices.workStreamService.parentEntities}
            childEntities={[
                { ObjectParentProperty: 'Contributors', ParentIdProperty: 'WorkStreamID', ChildIdProperty: 'ContributorUserID', ChildService: dataServices.contributorService },
                { ObjectParentProperty: 'CustomFields', ParentIdProperty: 'WorkStreamID', ChildIdProperty: 'CustomFieldDefnID', ChildService: dataServices.customFieldService }
            ]}
        />
    );
};
